import {
  BasicButton,
  ButtonGroup,
  Grid,
  GridItem,
  LegacyModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
  PrimaryButton,
  DateSelectField,
  TextInputField,
  CheckboxField,
  Toggle,
  TextareaField,
  Link,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { boolean, date, lazy, object, string } from "yup";

import { Credential } from "./type";

type CredentialModalProps = {
  index: number;
  credential?: Credential;
  onSave: (credential: Credential) => void;
  trigger: JSX.Element;
  onDelete?: () => void;
  allowHighest: boolean;
};
export function CredentialModal({
  credential,
  onSave,
  index,
  trigger,
  onDelete,
  allowHighest,
}: CredentialModalProps) {
  const { t } = useTranslation();
  const [isCurrentCredential, setIsCurrentCredential] = useState<boolean>(
    !!credential?.current
  );

  useEffect(() => {}, [isCurrentCredential]);

  const schema = object().shape({
    date_of_certification: lazy(() => {
      if (isCurrentCredential) {
        return date().notRequired();
      }
      return date()
        .max(new Date(), t("Invalid date"))
        .required()
        .label(t("Date received"));
    }),
    institution: string().trim().required().label(t("Institution")),
    certification: string().required().label(t("Credential title")),
    current: boolean(),
    highest_level: boolean(),
  });

  return (
    <ModalTrigger trigger={() => trigger}>
      {(close) => (
        <Formik
          initialValues={
            credential ?? {
              date_of_certification: null,
              institution: "",
              certification: "",
              description: "",
              current: false,
              highest_level: false,
              index,
            }
          }
          onSubmit={(credential) => {
            onSave(credential);
            close();
          }}
          validationSchema={schema}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <LegacyModal width="456px">
                <ModalHeader
                  close={close}
                  title={
                    credential ? t("Edit credential") : t("Add credential")
                  }
                  testID="add-credential-modal"
                />
                <ModalContent>
                  <Grid>
                    {allowHighest && (
                      <GridItem>
                        <Field
                          name="highest_level"
                          as={CheckboxField}
                          isRequired
                          testID="add-credential-modal-highest-level-checkbox"
                        >
                          <Trans>This is my highest level of credential</Trans>
                        </Field>
                      </GridItem>
                    )}
                    <GridItem>
                      <Field
                        name="institution"
                        label={t("Institution")}
                        as={TextInputField}
                        isRequired
                        testID="add-credential-modal-institution-input"
                      />
                    </GridItem>
                    <GridItem>
                      <Field
                        name="certification"
                        label={t("Credential title")}
                        as={TextInputField}
                        isRequired
                        description={t(
                          "E.g. Standard First Aid & CPR/AED Level C"
                        )}
                        testID="add-credential-modal-credential-title-input"
                      />
                    </GridItem>
                    <GridItem>
                      <Field
                        name="description"
                        label={t("Credential description")}
                        as={TextareaField}
                        isRequired
                        testID="add-credential-modal-credential-description-input"
                      />
                    </GridItem>

                    <GridItem
                      alignItems="left"
                      className={css({
                        flexDirection: "row",
                      })}
                    >
                      <Toggle
                        isSelected={isCurrentCredential}
                        onChange={(value) => {
                          setFieldValue("date_of_certification", null);
                          setFieldValue("current", value);
                          setIsCurrentCredential(value);
                        }}
                        testID="add-credential-modal-working-on-credential"
                      >
                        {t("I am currently working on this credential")}
                      </Toggle>
                    </GridItem>

                    {!isCurrentCredential && (
                      <GridItem>
                        <Field
                          label={t("Date received")}
                          name="date_of_certification"
                          isRequired
                          value={values.date_of_certification}
                          fullWidth
                          isApproximateDate
                          as={DateSelectField}
                          testID="add-credential-modal-date-received-select"
                        />
                      </GridItem>
                    )}
                  </Grid>
                </ModalContent>
                <ModalFooter
                  className={css({
                    justifyContent: onDelete ? "space-between" : "flex-end",
                  })}
                >
                  {onDelete && (
                    <Link
                      textStyle="global/body-secondary"
                      textColor="text-weak"
                      onPress={() => {
                        onDelete();
                        close();
                      }}
                      testID="add-credential-modal-delete-credential-button"
                    >
                      <Trans>Delete credential</Trans>
                    </Link>
                  )}
                  <ButtonGroup>
                    <BasicButton
                      size="medium"
                      onPress={close}
                      testID="add-credential-modal-cancel-button"
                    >
                      <Trans>Cancel</Trans>
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      testID="add-credential-modal-save-button"
                    >
                      <Trans>Save credential</Trans>
                    </PrimaryButton>
                  </ButtonGroup>
                </ModalFooter>
              </LegacyModal>
            </Form>
          )}
        </Formik>
      )}
    </ModalTrigger>
  );
}
